import React from 'react';
import { Skeleton } from 'antd';
import styled from 'styled-components';

const Root = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: calc(100vh - 64px);
  box-sizing: border-box;
  overflow: auto;
`;

const LeftPane = styled.div`
  flex: 1.45;
  min-width: 0;
  padding: 0 10px 16px;
`;

const RightPane = styled.div`
  flex: 1;
  min-width: 260px;
  padding: 0 10px 16px;
  border-left: 1px solid #e8e8e8;
`;

const TopBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const StudentMetaBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  padding: 10px 12px;
  background: #e7edf2;
  border-radius: 4px;
  margin-bottom: 12px;
`;

const SubmissionTab = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  margin-bottom: 6px;
  background: #f5f5f5;
  border-radius: 4px;
`;

const GradeRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 12px 0;
`;

const AiBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const QuestionBlock = styled.div`
  margin-bottom: 20px;
`;

const AccordionBar = styled.div`
  margin-top: 6px;
`;

const FilterPill = styled.div`
  padding: 10px 12px;
  background: #002f6c;
  border-radius: 20px;
  margin-bottom: 12px;

  .ant-skeleton-paragraph > li {
    background: rgba(255, 255, 255, 0.35) !important;
  }
`;

const SortRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const StudentRow = styled.div<{ $active?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  min-height: 36px;
  background: ${(p) => (p.$active ? 'rgba(14, 70, 183, 0.35)' : '#f0f0f0')};
`;

const FeedbackArea = styled.div`
  margin-top: 16px;
`;

const submissionKeys = [0, 1, 2];
const studentRows = [0, 1, 2, 3, 4, 5];
const questionBlocks = [0, 1];
const accordionKeys = [0, 1, 2];

const AssignmentReviewSkeleton: React.FC = () => (
  <Root>
    <LeftPane>
      <TopBar>
        <TitleRow>
          <Skeleton.Input active style={{ width: 180, height: 26 }} />
          <Skeleton.Button active style={{ width: 88, height: 30 }} />
        </TitleRow>
        <Skeleton.Button active style={{ width: 200, height: 32 }} />
      </TopBar>
      <StudentMetaBar>
        <Skeleton.Input active size="small" style={{ width: 140, height: 20 }} />
        <Skeleton.Input active size="small" style={{ width: 100, height: 20 }} />
        <Skeleton.Input active size="small" style={{ width: 120, height: 20 }} />
      </StudentMetaBar>
      {submissionKeys.map((k) => (
        <SubmissionTab key={k}>
          <Skeleton.Input active size="small" style={{ width: 160, height: 18 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Skeleton.Avatar active size="small" shape="square" />
            <Skeleton.Avatar active size="small" shape="square" />
          </div>
        </SubmissionTab>
      ))}
      <GradeRow>
        <Skeleton.Input active style={{ width: 220, height: 24 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Skeleton.Input active size="small" style={{ width: 72, height: 28 }} />
          <Skeleton.Button active style={{ width: 76, height: 30 }} />
        </div>
      </GradeRow>
      <AiBar>
        <Skeleton.Button active style={{ width: 220, height: 36 }} />
        <Skeleton.Input active size="small" style={{ width: 160, height: 32, borderRadius: 6 }} />
      </AiBar>
      {questionBlocks.map((q) => (
        <QuestionBlock key={q}>
          <Skeleton active title={false} paragraph={{ rows: 1, width: q === 0 ? '85%' : '70%' }} />
          <Skeleton.Input active style={{ width: '100%', height: 72, marginTop: 8, borderRadius: 6 }} />
          {accordionKeys.map((a) => (
            <AccordionBar key={a}>
              <Skeleton.Input active style={{ width: '100%', height: 30, borderRadius: 4 }} />
            </AccordionBar>
          ))}
          <Skeleton active title={false} paragraph={{ rows: 2, width: ['100%', '92%'] }} style={{ marginTop: 8 }} />
        </QuestionBlock>
      ))}
    </LeftPane>
    <RightPane>
      <FilterPill>
        <Skeleton active title={false} paragraph={{ rows: 1, width: '88%' }} />
      </FilterPill>
      <SortRow>
        <Skeleton.Input active size="small" style={{ width: 56, height: 18 }} />
        <Skeleton.Button active size="small" style={{ width: 72, height: 24 }} />
        <Skeleton.Button active size="small" style={{ width: 72, height: 24 }} />
        <Skeleton.Button active size="small" style={{ width: 64, height: 24 }} />
      </SortRow>
      {studentRows.map((r) => (
        <StudentRow key={r} $active={r === 0}>
          <Skeleton.Input active size="small" style={{ width: r === 0 ? 120 : 100, height: 18 }} />
          <Skeleton.Input active size="small" style={{ width: r === 0 ? 90 : 110, height: 16 }} />
        </StudentRow>
      ))}
      <FeedbackArea>
        <Skeleton.Input active size="small" style={{ width: 140, height: 16, marginBottom: 8 }} />
        <Skeleton.Input active style={{ width: '100%', height: 72, borderRadius: 6 }} />
      </FeedbackArea>
    </RightPane>
  </Root>
);

export default AssignmentReviewSkeleton;
